import { FleetAreaCode, type FleetDriverVehicle } from '@gb-ticket/shared-types';
import { type FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ListPagination } from '@/components/list-pagination';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { PersonChip } from '@/platform/components/person-chip';
import { formatDeDate } from '@/products/fleet/features/fleet-labels';
import { fleetAreaOn, useFleetAreas } from '@/products/fleet/features/use-fleet-areas';
import { fleetTableFrameClassName } from '@/products/fleet/features/fleet-panel';
import { useCreateFleetInstruction, useFleetDrivers } from '@/products/fleet/features/use-fleet';
import { fleetDriverPath } from '@/products/fleet/fleet-paths';

function vehicleLine(vehicle: FleetDriverVehicle): string | null {
  if (vehicle.status === 'current') {
    return vehicle.plates.join(', ');
  }
  if (vehicle.status === 'past') {
    return 'früher';
  }
  return null;
}

export function FleetDriversPage() {
  usePageTitle('Fahrer');
  const areas = useFleetAreas();
  const catalog = areas.isError ? undefined : areas.data;
  const complianceOn = fleetAreaOn(catalog, FleetAreaCode.COMPLIANCE);
  const instructionOn = fleetAreaOn(catalog, FleetAreaCode.INSTRUCTION);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const drivers = useFleetDrivers(page, search);
  const createInstruction = useCreateFleetInstruction();
  const trimmedSearch = search.trim();

  const [instruction, setInstruction] = useState({
    instructedOn: new Date().toISOString().slice(0, 10),
    title: 'UVV-Unterweisung',
    note: '',
  });
  const [selected, setSelected] = useState<Record<string, { displayName: string; email: string }>>(
    {},
  );

  const selectedList = useMemo(
    () => Object.entries(selected).map(([id, person]) => ({ id, ...person })),
    [selected],
  );

  function togglePerson(id: string, displayName: string, email: string) {
    setSelected((current) => {
      if (current[id]) {
        const next = { ...current };
        delete next[id];
        return next;
      }
      return { ...current, [id]: { displayName, email } };
    });
  }

  async function onInstructionSubmit(event: FormEvent) {
    event.preventDefault();
    if (selectedList.length === 0) {
      toast.error('Bitte mindestens eine Person für die UVV-Unterweisung auswählen.');
      return;
    }
    try {
      await createInstruction.mutateAsync({
        instructedOn: instruction.instructedOn,
        title: instruction.title.trim(),
        note: instruction.note.trim() || undefined,
        userIds: selectedList.map((person) => person.id),
      });
      setSelected({});
      setInstruction((current) => ({ ...current, note: '' }));
      toast.success('UVV-Unterweisung gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die UVV-Unterweisung konnte nicht gespeichert werden.',
      );
    }
  }

  return (
    <div className="space-y-8">
      <PlatformPageHeader
        title="Fahrer"
        description={
          complianceOn && instructionOn
            ? 'Führerscheinkontrolle und UVV-Unterweisung.'
            : complianceOn
              ? 'Führerscheinkontrolle.'
              : 'UVV-Unterweisung.'
        }
      />

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Fahrerakten</h2>
        <div className="space-y-1">
          <Label htmlFor="driver-search">Person suchen</Label>
          <Input
            id="driver-search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            placeholder="Name oder E-Mail"
          />
        </div>
        {drivers.isPending ? (
          <p className="text-sm text-muted-foreground">Liste wird geladen …</p>
        ) : null}
        {drivers.isError ? (
          <p className="text-sm text-destructive">
            {drivers.error instanceof ApiRequestError
              ? drivers.error.message
              : 'Fahrerakten konnten nicht geladen werden.'}
          </p>
        ) : null}
        {drivers.data?.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {trimmedSearch.length >= 2 ? 'Keine Person gefunden.' : 'Keine aktive Person.'}
          </p>
        ) : null}
        {drivers.data && drivers.data.items.length > 0 ? (
          <ul className={`${fleetTableFrameClassName} divide-y`}>
            {drivers.data.items.map((driver) => {
              const assignment = vehicleLine(driver.vehicle);
              return (
                <li
                  key={driver.userId}
                  className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <Link to={fleetDriverPath(driver.userId)} className="hover:underline">
                      <PersonChip
                        className="font-medium"
                        displayName={driver.displayName}
                        profilePhotoUrl={driver.profilePhotoUrl}
                      />
                    </Link>
                    {driver.technician ? (
                      <p className="text-sm font-medium text-primary">Monteur</p>
                    ) : null}
                    <p className="text-sm text-muted-foreground">
                      {driver.licenseClasses.length === 0
                        ? 'Akte leer'
                        : `Klassen: ${driver.licenseClasses.join(', ')}`}
                    </p>
                    {assignment ? (
                      <p className="text-sm text-muted-foreground">{assignment}</p>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-start gap-2 text-sm text-muted-foreground sm:items-end">
                    {complianceOn ? (
                      <p>Führerscheinkontrolle fällig: {formatDeDate(driver.nextCheckDue)}</p>
                    ) : null}
                    {instructionOn ? (
                      <p>UVV-Unterweisung fällig: {formatDeDate(driver.nextInstructionDue)}</p>
                    ) : null}
                    {instructionOn ? (
                      <Button
                        type="button"
                        variant={selected[driver.userId] ? 'default' : 'outline'}
                        size="sm"
                        onClick={() =>
                          togglePerson(driver.userId, driver.displayName, driver.email)
                        }
                      >
                        {selected[driver.userId] ? 'Für Unterweisung gewählt' : 'Für Unterweisung'}
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        ) : null}
        {drivers.data ? (
          <ListPagination
            page={drivers.data.page}
            pageSize={drivers.data.pageSize}
            total={drivers.data.total}
            onPageChange={setPage}
          />
        ) : null}
      </section>

      {instructionOn ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">UVV-Unterweisung</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <form className="space-y-3" onSubmit={onInstructionSubmit}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="instruction-date">Datum</Label>
                  <Input
                    id="instruction-date"
                    type="date"
                    value={instruction.instructedOn}
                    onChange={(event) =>
                      setInstruction((current) => ({
                        ...current,
                        instructedOn: event.target.value,
                      }))
                    }
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="instruction-title">Titel</Label>
                  <Input
                    id="instruction-title"
                    value={instruction.title}
                    onChange={(event) =>
                      setInstruction((current) => ({ ...current, title: event.target.value }))
                    }
                    required
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="instruction-note">Notiz (optional)</Label>
                <Input
                  id="instruction-note"
                  value={instruction.note}
                  onChange={(event) =>
                    setInstruction((current) => ({ ...current, note: event.target.value }))
                  }
                />
              </div>
              {selectedList.length > 0 ? (
                <p className="text-sm text-muted-foreground">
                  Teilnehmende: {selectedList.map((person) => person.displayName).join(', ')}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Personen in der Liste für die Unterweisung auswählen.
                </p>
              )}
              <Button type="submit" disabled={createInstruction.isPending}>
                UVV-Unterweisung speichern
              </Button>
            </form>
          </CardContent>
        </Card>
      ) : (
        <p className="text-sm text-muted-foreground">UVV-intern</p>
      )}
    </div>
  );
}
